import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  PlayerPackAddProjectionRow,
  PlayerPackPickedProjectionRow
} from '@bo-components/playerPacks';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import type { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { useCallback, useState } from 'react';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import type { PlayerPackFormValues } from '@library/types/playerPacks/PlayerPackFormSchema';
import type { PlayerPackProjection } from '@library/types/playerPacks/playerPacks';
import { StyledCard } from '@bo-components/common/StyledComponents';
import { usePlayerPacksPicks } from '@bo-hooks/playerPacks/usePlayerPacksPicks';

export interface PlayerPackPicksProps {
  projections: Array<PlayerPackProjection>;
  isProjectionsLoading: boolean;
  pickedProjections: Array<PlayerPackProjection>;
  setPickedProjections: React.Dispatch<
    React.SetStateAction<Array<PlayerPackProjection>>
  >;
  formMethods: UseFormReturn<PlayerPackFormValues>;
  fieldMethods: UseFieldArrayReturn<PlayerPackFormValues, 'picks'>;
}

export const PlayerPackPicks = ({
  projections,
  isProjectionsLoading,
  pickedProjections,
  setPickedProjections,
  formMethods,
  fieldMethods
}: PlayerPackPicksProps) => {
  const [isRowSelected, setIsRowSelected] = useState<Record<string, boolean>>(
    {}
  );

  const { handleAddProjection, handleRemoveProjection } = usePlayerPacksPicks({
    setPickedProjections,
    setIsRowSelected,
    form: formMethods,
    fields: fieldMethods
  });

  // Sensors for detecting pointer actions for dragging
  const sensors = useSensors(
    useSensor(PointerSensor) // Use PointerSensor for mouse/touch dragging
  );

  // Function to handle drag end event
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      // If no valid destination or the position hasn't changed, do nothing
      if (!over || active.id === over.id) {
        return;
      }

      // Find the old index and new index of the dragged row
      const oldIndex = pickedProjections.findIndex(
        (item) => item.projectionId === active.id
      );
      const newIndex = pickedProjections.findIndex(
        (item) => item.projectionId === over.id
      );

      fieldMethods.move(oldIndex, newIndex);

      // Reorder the projections based on the drag-and-drop result
      const updatedProjections = arrayMove(
        pickedProjections,
        oldIndex,
        newIndex
      );

      // Update the projections state
      setPickedProjections(updatedProjections);
    },
    [pickedProjections, fieldMethods, setPickedProjections]
  );

  return (
    <div className="flex h-screen flex-row gap-4">
      <StyledCard className="bg-soFresh-110 flex h-full flex-[3] flex-col items-start justify-start gap-6 overflow-auto p-0 pb-4">
        <div className="font-heebo bg-soFresh-120 text-soClean-170 w-full p-4 font-medium">
          Projections
        </div>
        {isProjectionsLoading && (
          <Loading loadingMessage="Loading Projections" />
        )}
        {!isProjectionsLoading &&
          projections?.length > 0 &&
          projections.map((projection) => {
            return (
              <PlayerPackAddProjectionRow
                key={projection.projectionId}
                projection={projection}
                onAddProjection={handleAddProjection}
                isProjectionPicked={!!isRowSelected[projection.projectionId]}
              />
            );
          })}
        {!isProjectionsLoading && projections?.length === 0 && (
          <NoResults
            label="No projections were found"
            className="bg-soFresh-110 mt-4 flex-wrap"
            classNameInner="bg-soFresh-110"
          />
        )}
      </StyledCard>
      <StyledCard
        title=""
        className="bg-soFresh-110 flex h-full flex-[5] flex-col items-start justify-start gap-6 overflow-auto p-0"
      >
        <table className="w-full table-auto space-y-10 pb-4">
          <thead className="font-heebo text-soClean-170 bg-soFresh-120 w-full text-sm font-medium">
            <tr className="w-full">
              <th className="p-4 pl-12 text-left">Player</th>
              <th className="p-4 text-left">Projection</th>
              <th className="p-4 text-left">Stat Type</th>
              <th className="p-4 text-left">Prop</th>
              <th className="p-4" aria-label="remove" />
            </tr>
          </thead>
          {pickedProjections?.length > 0 ? (
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <SortableContext
                items={pickedProjections.map(
                  (projection) => projection.projectionId
                )}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {pickedProjections.map((projection, index) => (
                    <PlayerPackPickedProjectionRow
                      key={projection.projectionId}
                      projection={projection}
                      onRemoveProjection={handleRemoveProjection}
                      setValue={formMethods.setValue}
                      temporaryRank={index}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </DndContext>
          ) : (
            <NoResults
              label="Select projections for the lineup"
              className="bg-soFresh-110 flex-wrap"
              classNameInner="bg-soFresh-110"
            />
          )}
        </table>
      </StyledCard>
    </div>
  );
};
